import { CurrencyPipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { AnnualCostByScenarios } from '@app/core/models/demand/demand-request';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { AppState } from '@app/state';
import { selectDateFormat } from '@app/state/auth/auth.selectors';
import { setSelectedDemand } from '@app/state/demand/demand.actions';
import { selectedDemand } from '@app/state/demand/demand.selectors';
import { DerivativeCardInfo } from '@interfaces/a-organizar/derivativeCardInfo';
import { DerivativeSummary } from '@interfaces/a-organizar/derivativeSummary';
import { IdValue } from '@interfaces/a-organizar/idValue';
import {
  NdfDemandsCalculationsFiltered,
  NdfDemandsCalculationsOperation,
} from '@interfaces/a-organizar/ndfDemandsCalculationsFiltered';
import { NdfOperationFilteredSummary } from '@interfaces/a-organizar/ndfOperationsFilteredSummary';
import { Company } from '@interfaces/common/company';
import { InternationalCurrency } from '@interfaces/common/internationalCurrencies';
import { Option } from '@interfaces/common/option';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DerivativesCardInfoDataService } from '@services/data/derivatives-card-info/derivatives-card-info-data.service';
import { DerivativesSummaryDataService } from '@services/data/derivatives-summary/derivatives-summary-data.service';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { CompanyService } from '@services/http/company.service';
import { DerivativeReportHttpService } from '@services/http/derivative-report/derivative-report-http.service';
import {
  DerivativeOperationQuoteFromAi,
  NdfCalculatorService,
} from '@services/http/ndf-calculator.service';
import {
  DerivativeMtmHttpService,
  DerivativeOperationDemandFilterDto,
  DerivativeOperationDemandWithMtmDto,
} from '@src/app/core/services/http/derivatives/mtm/derivatives-mtm.service';
import { CnpjFormatter } from '@src/app/core/utils/cnpj.utils';
import { openFileInNewTab } from '@src/app/core/utils/download-file.utils';
import { nameof } from '@src/app/core/utils/nameof.utils';
import ModalComexOperationDetailsComponent from '@src/app/features/dashboard/comex/pages/modal-comex-operation-details/modal-comex-operation-details.component';
import { NdfEditDemandComponent } from '@src/app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-edit-demand/ndf-edit-demand.component';
import { CreateOperatinsWithCogModalComponent } from '@src/app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-management/create-operations-with-cog/create-operations-with-cog-modal.component';
import DerivativeMtmResultsModalComponent from '@src/app/features/dashboard/control/derivative-management/mtm/modal/derivative-mtm-results.modal';
import { ModalLoanDetailsComponent } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { ModalConfirmComponent } from '@src/app/shared/components/modal/modal-confirm/modal-confrm.component';
import { ModalDerivativeAttachmentsComponent } from '@src/app/shared/components/modal/modal-derivative-attachments/modal-derivative-attachments.component';
import { ModalOkComponent } from '@src/app/shared/components/modal/modal-ok/modal-ok.component';
import { Chart } from 'chart.js';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize, first } from 'rxjs';
import { NdfManagementGridComponent } from './ndf-management-grid/ndf-management-grid.component';
import { OperationAction } from './ndf-management-item/ndf-management-item.component';
import { DemandOutput } from './ndf-management.types';

export interface LineChartData {
  backgroundColor?: string;
  borderColor?: string;
  data?: number[];
  fill?: boolean;
  label?: string;
}

@Component({
  selector: 'app-ndf-management',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  templateUrl: './ndf-management.component.html',
  styleUrls: ['./ndf-management.component.scss'],
})
export class NdfManagementComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  demandId: string = '';
  disableFees: boolean = false;
  demandPermissions = Permissions.DerivativeCalculator;
  derivativeSummary: DerivativeSummary | null = null;
  derivativeCardInfo: DerivativeCardInfo | null = null;
  demand: NdfDemandsCalculationsFiltered | null = null;
  operationToHireSummary: NdfOperationFilteredSummary | null = null;

  operationToHire: NdfDemandsCalculationsOperation | null = null;
  internationalCurrencies: InternationalCurrency[] = [];
  currentDate: Date = new Date();
  dateTimeQuoted: string = '';
  dateTimeHired: string = '';
  companyOptions: Option[] = [];
  selectedOperation: NdfDemandsCalculationsOperation | null = null;
  selectedOperationDate: Date | null = null;
  selectedOperationDueAt: Date | null = null;
  dateFormat: string = 'dd/MM/yyyy';
  ndfLineChart: Chart | null = null;
  editReference: boolean = false;

  @ViewChild('editDemand')
  ndfEditDemandComponent!: NdfEditDemandComponent;

  @ViewChild('grid') grid?: NdfManagementGridComponent;

  @ViewChild('loanDetailsModal') loanDetailsModalRef!: ModalLoanDetailsComponent;
  @ViewChild('comexDetailsModal') comexDetailsModalRef!: ModalComexOperationDetailsComponent;

  @ViewChild('attachmentsModal') attachmentsModal?: ModalDerivativeAttachmentsComponent;
  @ViewChild('resultsModal') resultsModal!: DerivativeMtmResultsModalComponent;

  toggleEditReference() {
    if (!this.checkIfDemandIsSavedAndThrowError()) {
      return;
    }
    this.ndfEditDemandComponent.open(this.demandId);
  }

  handleSuccessUpdate() {
    this.getDemandById();
    this.ndfEditDemandComponent.close();
  }

  operationQuoteHireProps = nameof<{
    data: string;
    bankOperatorName: string;
  }>();
  operationQuoteForm = this.formBuilder.group({
    [this.operationQuoteHireProps.data]: ['', Validators.required],
    [this.operationQuoteHireProps.bankOperatorName]: [''],
  });
  operationHireForm = this.formBuilder.group({
    [this.operationQuoteHireProps.data]: ['', Validators.required],
    [this.operationQuoteHireProps.bankOperatorName]: [''],
  });

  demandProps = nameof<{
    reference: string;
    budgetRate: string;
    transactionValue: string;
  }>();
  demandForm = this.formBuilder.group({
    [this.demandProps?.reference]: ['', Validators.required],
    [this.demandProps?.budgetRate]: ['0', Validators.required],
  });

  // Filtros para operações
  operationStatusOptions: Option[] = [
    { label: 'Status da Operação', value: '' },
    { label: 'Simulado', value: 'Simulated' },
    { label: 'Cotado', value: 'Quoted' },
    { label: 'Contratado', value: 'Hired' },
  ];
  selectedOperationStatus: Option = this.operationStatusOptions[0];

  financialStatusOptions: Option[] = [
    { label: 'Status Financeiro', value: '' },
    { label: 'Aberto', value: 'Open' },
    { label: 'Liquidado', value: 'Settled' },
    { label: 'Reversão', value: 'Reversed' },
  ];
  selectedFinancialStatus: Option = this.financialStatusOptions[0];

  compositionOptions: Option[] = [
    { label: 'Composição', value: '' },
    { label: 'Integral', value: 'Full' },
    { label: 'Parcial', value: 'Partial' },
  ];
  selectedComposition: Option = this.compositionOptions[0];

  public isFiltering = false;
  filteredOperations: NdfDemandsCalculationsOperation[] = [];
  private filterWorker: Worker | undefined;

  // Adicione a propriedade para o filtro de texto
  textFilter: string = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private textFilterTimeout: any;

  // Adicionar essas variáveis para armazenar os IDs e sequential IDs localmente
  comexOperationId: string | undefined = undefined;
  loanOperationId: string | undefined = undefined;
  comexSequentialId: string | undefined = undefined;
  loanSequentialId: string | undefined = undefined;

  constructor(
    translateService: TranslateService,
    private ndfCalculatorService: NdfCalculatorService,
    private confirmDialogService: ConfirmDialogService,
    private dialogService: DialogService,
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastHelperService,
    private utilsService: UtilsService,
    private companyService: CompanyService,
    private currencyPipe: CurrencyPipe,
    private derivativesSummaryDataService: DerivativesSummaryDataService,
    private derivativesCardInfoDataService: DerivativesCardInfoDataService,
    private derivativeReportHttpService: DerivativeReportHttpService,
    private cdr: ChangeDetectorRef,
    private derivativeMtmHttp: DerivativeMtmHttpService,

    store: Store<AppState>
  ) {
    super(translateService, store);
  }

  private destroyCharts(): void {
    if (this.ndfLineChart) {
      this.ndfLineChart.destroy();
      this.ndfLineChart = null;
    }
  }

  override ngOnDestroy(): void {
    this.destroyCharts();
  }

  async ngOnInit() {
    if (this.store) {
      this.dateFormat = (await this.store
        .select(selectDateFormat)
        .pipe(first())
        .toPromise()) as string;
    }
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/dashboard',
      },
      {
        label: this.t(this.tk.Management.Derivatives),
      },
      {
        label: this.t(this.tk.Management.NdfDemandManagement),
        routerLink: '/dashboard/ndf-calculator-list',
      },
    ];

    // Novo: verifica se há filtro na query string
    this.route.queryParams.subscribe(params => {
      if (params['filter']) {
        this.textFilter = params['filter'];
        this.applyFilter();
      }
    });

    this.getDemandById();
    this.getInternationalCurrencies();
    this.getCompanysOptions();

    if (typeof Worker !== 'undefined') {
      this.filterWorker = new Worker(new URL('./filter.worker', import.meta.url));
      this.filterWorker.onmessage = ({ data }) => {
        this.filteredOperations = data;
        this.isFiltering = false;
        //this.setIsLoading(false);
        this.cdr.detectChanges();
      };
    }
  }
  currencyCode?: string;
  getDemandById(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.demandId = params['id'];
        this.setIsLoading(true);
        this.ndfCalculatorService.getDemandById(this.demandId).subscribe({
          next: response => {
            this.setIsLoading(false);
            if (response) {
              // Salvar os IDs e sequential IDs do comex e loan localmente logo após carregar
              this.comexOperationId = response?.comexOperationId || undefined;
              this.loanOperationId = response?.loanOperationId || undefined;
              this.comexSequentialId = response?.comexOperation?.sequentialId || undefined;
              this.loanSequentialId = response?.loanOperation?.sequentialId || undefined;

              if (
                !response?.comexOperationId &&
                !response?.loanOperationId &&
                response.operationType !== 'Indicative'
              ) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const modalRef = this.dialogService.open(ModalOkComponent, {
                  data: {
                    title: this.translate(this.t(this.tk.Management.LinkOperationToLoanOrComex)),
                  },
                });
              }

              this.currencyCode = response?.internationalCurrency?.currencyCode;
              const demand = {
                ...response,
                annualCostByScenarios: response?.operations?.length
                  ? response?.operations.find(operation => operation?.annualCostByScenarios)
                      ?.annualCostByScenarios || []
                  : [],
              };
              this.demand = this.sortDemandByOrder(demand);
              this.applyFilter();
              if (this.store) {
                this.store.dispatch(
                  setSelectedDemand({
                    demand: this.demand,
                  })
                );
              }
              this.demandForm.patchValue({
                [this.demandProps.reference]: this.demand.reference,
              });
              this.demandForm.patchValue({
                [this.demandProps.budgetRate]: this.demand.budgetRate?.toString() as string,
              });
              this.saveDemandLocally(this.demand);
              this.handleNdfChart();
              this.applyFilter();
            }
          },
          error: error => {
            this.setIsLoading(false);

            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingDemand),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
      } else {
        const localDemand = localStorage.getItem('selectedDemand');
        if (this.store) {
          this.store.select(selectedDemand).subscribe(demand => {
            this.demand = demand || JSON.parse(localDemand || '{}');
            if (!Object.keys(this.demand as object)?.length) {
              this.router.navigate(['dashboard/ndf-calculator/list']);
            }

            if (!this.demand?.operations.length) this.addSimulation();
          });
        }
        this.handleNdfChart();
      }
    });
  }

  sortDemandByOrder(demand: NdfDemandsCalculationsFiltered): NdfDemandsCalculationsFiltered {
    if (!demand?.operations) return demand;

    const sortedOperations = [...demand.operations].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return {
      ...demand,
      operations: sortedOperations,
    } as NdfDemandsCalculationsFiltered;
  }

  saveDemandLocally(demand: NdfDemandsCalculationsFiltered): void {
    localStorage.setItem('selectedDemand', JSON.stringify(demand));
  }

  handleNdfChart() {
    this.ndfLineChart?.destroy();

    const data = this.demand?.operations?.map((operation, i) => {
      return {
        label: this.t(this.tk.Management.NdfOperation) + ' ' + (i + 1),
        data: operation?.annualCostByScenarios?.map(annualCost => annualCost.annualCost),
        fill: false,
      };
    });

    const ctx = document.getElementById('ndf-line-chart') as HTMLCanvasElement;

    const firstOperation = data?.[0];
    const labels = firstOperation?.data?.map(
      (_, i) => `${this.t(this.tk.Management.NdfScenario)} ${this.getLetterFromIndex(i)}`
    );

    this.ndfLineChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: data || [],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: {
              display: true,
              text: '',
            },
          },
          y: {
            title: {
              display: true,
              text: this.t(this.tk.Management.NdfAnnualCost),
            },
          },
        },
      },
    }) as Chart;
  }

  getLetterFromIndex(index: number): string {
    let letters = '';
    while (index >= 0) {
      letters = String.fromCharCode((index % 26) + 65) + letters;
      index = Math.floor(index / 26) - 1;
    }
    return letters;
  }
  handleInputText(inputData: IdValue, key: string) {
    switch (key) {
      case this.demandProps.reference:
        if (this.demand)
          this.demand = {
            ...this.demand,
            reference: inputData.value as string,
          };
        break;
    }
  }

  handleInputNumber(inputData: IdValue, key: string): void {
    switch (key) {
      case 'transactionValue':
        if (this.demand)
          this.demand = {
            ...this.demand,
            transactionValue: inputData.value as number,
          };
        break;
      case 'budgetRate':
        if (this.demand)
          this.demand = {
            ...this.demand,
            budgetRate: inputData.value as number,
          };
        break;
      default:
        break;
    }
  }
  lastDemandUpdate: NdfDemandsCalculationsFiltered | null = null;
  private pendingSimulationRequest = false;
  private simulationTimeoutId: number | null = null;

  handleUpdatedDemand(output: DemandOutput | null): void {
    // Se não tiver conteúdo ou já tiver atualizado recentemente com os mesmos dados, retorna
    const demand = output?.demand;
    if (!demand) return;

    // Atualiza state sem simulação quando não solicitado
    if (!output?.postSimulation) {
      this.demand = demand;
      this.updateStoreAndSaveLocally(this.demand);
      return;
    }

    // Compara com a última demanda para evitar chamadas desnecessárias
    if (JSON.stringify(this.lastDemandUpdate) === JSON.stringify(demand)) {
      return;
    }

    this.lastDemandUpdate = JSON.parse(JSON.stringify(demand));
    this.disableFees = true;

    // Cancela qualquer requisição pendente
    if (this.simulationTimeoutId !== null) {
      window.clearTimeout(this.simulationTimeoutId);
    }

    // Agenda nova requisição com debounce reduzido para melhorar performance
    this.simulationTimeoutId = window.setTimeout(() => {
      // Só executa se não houver outra requisição pendente
      if (!this.pendingSimulationRequest) {
        this.pendingSimulationRequest = true;
        // Removendo o loading durante a digitação do usuário
        // this.setIsLoading(true);

        const formatedDemand = this.formatDemand(demand);
        this.ndfCalculatorService.postDerivativesDemandSimulation(formatedDemand).subscribe({
          next: response => {
            this.pendingSimulationRequest = false;
            this.simulationTimeoutId = null;
            // this.setIsLoading(false);

            if (response) {
              this.processSimulationResponse(response, demand);
            }
            this.disableFees = false;
          },
          error: (error: { errors?: Array<{ message: string }> }) => {
            this.pendingSimulationRequest = false;
            this.simulationTimeoutId = null;
            // this.setIsLoading(false);

            console.error('postDerivativesDemandSimulation error: ', error);
            this.toast.showToastError(
              this.t(this.tk.Management.ErrorCreatingDemandSimulation),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );

            this.demand = this.sortDemandByOrder(demand as NdfDemandsCalculationsFiltered);
            this.handleNdfChart();
            this.disableFees = false;
          },
          complete: () => {
            this.disableFees = false;
            this.applyFilter();
          },
        });
      }
    }, 200); // Reduzido de 500ms para 200ms para melhorar a responsividade
  }

  // Método auxiliar para processar a resposta da simulação
  private processSimulationResponse(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    response: any,
    originalDemand: NdfDemandsCalculationsFiltered
  ): void {
    this.demand = {
      ...response,
      id: originalDemand.id,
      sequentialId: originalDemand.sequentialId,
      company: originalDemand.company,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      operations: response.operations.map((operation: any) => {
        const { reference, ...restOperation } = operation;
        const op = originalDemand.operations.find(op => op.id === operation.id);
        return {
          ...restOperation,
          operationStatus: op?.operationStatus || 'Simulated',
          financialStatus: op?.financialStatus,
          composition: op?.composition,
          sequentialId: op?.sequentialId || '',
          controlNameBlocked: op?.controlNameBlocked || [],
          controlNameHighlighted: [],
          reference: op?.reference || reference,
          settlementDate: op?.settlementDate,
          settlementNotional: op?.settlementNotional,
          settlementRate: op?.settlementRate,
          settlementSpot: op?.settlementSpot,
          totalAmount: op?.totalAmount,
          withholdingTax: op?.withholdingTax,
          incomeTaxPayable: op?.incomeTaxPayable,
          netAmount: op?.netAmount,
          strikeBuy: op?.strikeBuy,
          strikeSell: op?.strikeSell,
          premium: op?.premium,
          numberOfInstallments: op?.numberOfInstallments,
          activeIndexer: op?.activeIndexer,
          passiveIndexer: op?.passiveIndexer,
          activeFixedRatePerYear: op?.activeFixedRatePerYear,
          passiveFixedRatePerYear: op?.passiveFixedRatePerYear,
          operationStrategyType: op?.operationStrategyType,
          dayCountConvention: op?.dayCountConvention,
        };
      }),
    };

    this.demand = this.sortDemandByOrder(this.demand as NdfDemandsCalculationsFiltered);
    this.handleNdfChart();
    this.updateStoreAndSaveLocally(this.demand);
  }

  private updateStoreAndSaveLocally(demand: NdfDemandsCalculationsFiltered): void {
    if (this.store) {
      this.store.dispatch(
        setSelectedDemand({
          demand: demand,
        })
      );
    }
    this.saveDemandLocally(demand);
  }

  getInternationalCurrencies(): void {
    this.setIsLoading(true);
    this.ndfCalculatorService.getInternationalCurrencies().subscribe({
      next: response => {
        this.setIsLoading(false);
        if (response) {
          this.internationalCurrencies = response;
        }
      },
      error: error => {
        this.setIsLoading(false);
        console.error('Error getting international currencies: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingInternacionalCurrencies),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  cancelDemand(): void {
    if (this.store) {
      this.store.dispatch(
        setSelectedDemand({
          demand: null,
        })
      );
    }
    localStorage.removeItem('selectedDemand');

    if (window.history.length > 1) {
      window.history.back();
    } else {
      this.router.navigate(['dashboard/ndf-calculator/list']);
    }
  }

  get hasSelectedOperations(): boolean {
    return this.demand?.operations?.some(x => x.selected) ?? false;
  }

  openMtmForDemand(): void {
    if (!this.demand) return;
    const companyId = this.demand.companyId || '';
    const payload: DerivativeOperationDemandFilterDto = {
      page: 1,
      pageSize: 100,
      companyId,
      derivativeOperationDemandId: this.demand.id || undefined,
    };
    this.utilsService.setIsLoading(true, 'Estamos buscando as informações, aguarde...');
    this.derivativeMtmHttp
      .getFilteredWithMtm(payload)
      .pipe(
        first(),
        finalize(() => this.utilsService.setIsLoading(false))
      )
      .subscribe(result => {
        if (result) {
          this.resultsModal?.open(result as DerivativeOperationDemandWithMtmDto, companyId);
        }
      });
  }

  openMtmForSelected(): void {
    if (!this.demand) return;
    const selectedIds = (this.demand.operations || [])
      .filter(op => op.selected && op.id)
      .map(op => op.id as string);
    if (selectedIds.length === 0) return;

    const companyId = this.demand.companyId || '';
    const payload: DerivativeOperationDemandFilterDto = {
      page: 1,
      pageSize: 100,
      companyId,
      derivativeOperationDemandId: this.demand.id || undefined,
      operationIds: selectedIds,
    };
    this.utilsService.setIsLoading(true, 'Estamos buscando as informações, aguarde...');
    this.derivativeMtmHttp
      .getFilteredWithMtm(payload)
      .pipe(
        first(),
        finalize(() => this.utilsService.setIsLoading(false))
      )
      .subscribe(result => {
        if (result) {
          this.resultsModal?.open(result as DerivativeOperationDemandWithMtmDto, companyId);
        }
      });
  }

  handleOperationAction(operationAction: OperationAction) {
    const operationIndex = operationAction.index as number;
    const controlName = operationAction.controlName as keyof NdfDemandsCalculationsOperation;

    const valueToReplicate = controlName
      ? this.demand?.operations[operationIndex][controlName]
      : null;

    const financialInstitutionId = this.demand?.operations[operationIndex].financialInstitutionId;
    let formatedDemand = this.demand;

    switch (operationAction.action) {
      case 'save-demand':
        this.registerDemand();
        break;
      case 'remove':
        this.removeOperation(operationAction.item);
        break;
      case 'quote':
        this.handleQuoteOperation(operationAction.item, operationAction.index ?? 0);
        break;
      case 'hire':
        this.handleHireOperation(operationAction.item);
        break;
      case 'pin-unpin':
        this.demand = {
          ...this.demand,
          operations:
            this.demand?.operations.map((operation, index) => {
              if (index === operationAction.index) {
                return {
                  ...operation,
                  colapsed: !operation.colapsed,
                } as NdfDemandsCalculationsOperation;
              }
              return operation as NdfDemandsCalculationsOperation;
            }) || [],
        } as NdfDemandsCalculationsFiltered;
        break;
      case 'check-uncheck':
        this.demand = {
          ...this.demand,
          operations:
            this.demand?.operations.map((operation, index) => {
              if (index === operationAction.index) {
                return {
                  ...operation,
                  selected: !operation.selected,
                } as NdfDemandsCalculationsOperation;
              }
              return operation as NdfDemandsCalculationsOperation;
            }) || [],
        } as NdfDemandsCalculationsFiltered;
        break;
      case 'block':
        this.demand = {
          ...this.demand,
          operations: this.demand?.operations.map((operation, index) => {
            if (index === operationAction.index) {
              return {
                ...operation,
                controlNameBlocked: operation?.controlNameBlocked?.includes(controlName || '')
                  ? operation?.controlNameBlocked?.filter(name => name !== controlName)
                  : [...(operation?.controlNameBlocked || []), controlName],
              } as NdfDemandsCalculationsOperation;
            }
            return operation as NdfDemandsCalculationsOperation;
          }),
        } as NdfDemandsCalculationsFiltered;
        break;
      case 'highlight':
        this.demand = {
          ...this.demand,
          operations: this.demand?.operations.map((operation, index) => {
            if (index === operationIndex) {
              return {
                ...operation,
                [controlName]: valueToReplicate,
                controlNameHighlighted: operation?.controlNameHighlighted?.includes(
                  controlName || ''
                )
                  ? operation?.controlNameHighlighted?.filter(name => name !== controlName)
                  : [...(operation?.controlNameHighlighted || []), controlName],
              } as NdfDemandsCalculationsOperation;
            } else {
              return {
                ...operation,
                [controlName]: operation?.controlNameBlocked?.includes(controlName)
                  ? operation[controlName]
                  : valueToReplicate,
                controlNameHighlighted: [],
                financialInstitutionId:
                  controlName === 'financialInstitution'
                    ? financialInstitutionId
                    : operation?.financialInstitutionId,
              } as NdfDemandsCalculationsOperation;
            }
          }),
        } as NdfDemandsCalculationsFiltered;
        formatedDemand = this.formatDemand(this.demand);
        if (this.demand?.operations.every(item => item.financialInstitutionId && item.dueAt)) {
          this.postSimulatedDemand(formatedDemand);
        }
        break;
      case 'move-up':
      case 'move-down':
      case 'move-last':
        this.demand = this.moveOperation(operationAction.item, operationAction.action);
        break;
      case 'revert-liquidation-to-open':
        this.handleRevertSettlementOperationToHire(operationAction.item.id as string);
        break;
      case 'revert-quoted-to-simulated':
        this.handleRevertQuotedToSimulatedOperation(operationAction.item.id as string);
        break;
      case 'revert-hired-to-quoted':
        this.handleRevertHiredToQuoteOperation(operationAction.item, operationAction.index ?? 0);
        break;
      case 'attachments':
        this.attachmentsModal?.open(operationAction.item.id as string);
        break;
    }

    this.applyFilter();
  }

  onAttachmentUploaded(): void {
    // Refresh data or perform any necessary action after attachment upload
    this.toast.showToastSuccess('Anexos atualizados com sucesso');
  }

  handleRevertQuotedToSimulatedOperation(id: string) {
    if (!this.checkIfDemandIsSavedAndThrowError()) {
      return;
    }
    if (!id) return;

    const doRevert = () => {
      const modalRef = this.dialogService.open(ModalConfirmComponent, {
        data: {
          title: this.t(this.tk.Management.StatusReversion),
          description: this.t(this.tk.Management.ConfirmReversalQuotationToSimulated),
          btnFirst: this.t(this.tk.General.Cancel),
          btnSecond: this.t(this.tk.General.Confirm),
        },
      });

      if (modalRef) {
        modalRef.onClose.subscribe({
          next: result => {
            if (result?.confirmed) {
              this.ndfCalculatorService.putOperationStatusSimulated(id).subscribe({
                next: () => {
                  this.setIsLoading(false);
                  this.getDemandById();
                  // Force refresh of operation items after status change
                  this.cdr.detectChanges();
                },
                error: error => {
                  this.setIsLoading(false);
                  this.toast.showToastError(
                    this.t(this.tk.Management.ErrorLoadingOperation),
                    error?.errors?.length
                      ? error.errors[0].message
                      : this.t(this.tk.General.RequestError)
                  );
                },
              });
            }
          },
        });
      }
    };

    this.hasOperationsNotSaved ? this.registerDemand(doRevert) : doRevert();
  }

  handleRevertHiredToQuoteOperation(operation: NdfDemandsCalculationsOperation, index: number) {
    if (!this.checkIfDemandIsSavedAndThrowError()) {
      return;
    }
    if (!operation.id) return;
    const doRevert = () => {
      const modalRef = this.dialogService.open(ModalConfirmComponent, {
        data: {
          title: this.t(this.tk.Management.StatusReversion),
          description: this.t(this.tk.Management.ConfirmRevertContractStatusQuoted),
          btnFirst: this.t(this.tk.General.Cancel),
          btnSecond: this.t(this.tk.General.Confirm),
        },
      });

      if (modalRef) {
        modalRef.onClose.subscribe({
          next: result => {
            if (result?.confirmed) {
              this.handleQuoteOperation(operation, index);
              // Force refresh of operation items after status change
              this.cdr.detectChanges();
            }
          },
        });
      }
    };

    this.hasOperationsNotSaved ? this.registerDemand(doRevert) : doRevert();
  }

  handleRevertSettlementOperationToHire(id: string) {
    if (!this.checkIfDemandIsSavedAndThrowError()) {
      return;
    }

    if (!id) return;

    const doRevert = () => {
      const modalRef = this.dialogService.open(ModalConfirmComponent, {
        data: {
          title: this.t(this.tk.Management.StatusReversion),
          description: this.t(this.tk.Management.ConfirmReverseSettlementSetStatusOpen),
          btnFirst: this.t(this.tk.General.Cancel),
          btnSecond: this.t(this.tk.General.Confirm),
        },
      });

      if (modalRef) {
        modalRef.onClose.subscribe({
          next: result => {
            if (result?.confirmed) {
              this.ndfCalculatorService.putNdfRevertLiquidate(id).subscribe({
                next: () => {
                  this.setIsLoading(false);
                  this.getDemandById();
                  // Force refresh of operation items after status change
                  this.cdr.detectChanges();
                },
                error: error => {
                  this.setIsLoading(false);
                  this.toast.showToastError(
                    this.t(this.tk.Management.ErrorLoadingOperation),
                    error?.errors?.length
                      ? error.errors[0].message
                      : this.t(this.tk.General.RequestError)
                  );
                },
              });
            }
          },
        });
      }
    };

    this.hasOperationsNotSaved ? this.registerDemand(doRevert) : doRevert();
  }
  moveOperation(
    operation: NdfDemandsCalculationsOperation,
    direction: 'move-up' | 'move-down' | 'move-last'
  ): NdfDemandsCalculationsFiltered {
    const operations = this.demand!.operations.map(op => ({ ...op }));
    const currentIndex = operations.findIndex(op => op.order === operation.order);

    if (currentIndex !== -1) {
      if (direction === 'move-last') {
        const [currentOperation] = operations.splice(currentIndex, 1);

        for (let i = currentIndex; i < operations.length; i++) {
          operations[i].order = i + 1;
        }

        currentOperation.order = operations.length + 1;

        operations.push(currentOperation);
      } else {
        const newIndex = direction === 'move-up' ? currentIndex - 1 : currentIndex + 1;

        if (newIndex >= 0 && newIndex < operations.length) {
          const currentOperation = { ...operations[currentIndex] };
          const targetOperation = { ...operations[newIndex] };

          [currentOperation.order, targetOperation.order] = [
            targetOperation.order,
            currentOperation.order,
          ];

          operations[currentIndex] = currentOperation;
          operations[newIndex] = targetOperation;
        }
      }
    }

    let demand = {
      ...this.demand,
      operations,
    } as NdfDemandsCalculationsFiltered;

    demand = this.sortDemandByOrder(demand);

    return demand;
  }
  getCompanysOptions(): void {
    this.companyService.getAllCompanys().subscribe({
      next: response => {
        this.setIsLoading(false);
        const options = (response?.data || []).map(
          (company: Company) =>
            ({
              label: company.displayName,
              value: company.id,
            }) as Option
        );
        this.companyOptions = options;
      },
      error: error => {
        this.setIsLoading(false);
        console.error('error: ', error);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingCompanys),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  get demandCompany(): string {
    return this.companyOptions.find(option => option.value === this.demand?.companyId)?.label || '';
  }

  @ViewChild('modalHireConfirmation')
  modalHireConfirmation?: DialogComponent;

  @ViewChild('modalHire')
  modalHire?: DialogComponent;

  handleHireOperation(operation: NdfDemandsCalculationsOperation) {
    if (!this.checkIfDemandIsSavedAndThrowError()) {
      return;
    }
    const doHire = () => {
      this.operationToHire = operation;
      this.selectedOperationDate = this.formatDateStringToDate(operation.dateTimeQuoted || '');
      this.selectedOperationDueAt = this.formatDateStringToDate(operation.dueAt || '');
      this.dateTimeHired = this.currentDate.toISOString();
      this.operationHireForm.patchValue({
        [this.operationQuoteHireProps.data]: this.dateTimeHired,
      });

      this.derivativeCardInfo = this.derivativesCardInfoDataService.mapDemandToDerivativeCardInfo(
        this.demand,
        this.operationToHire
      );

      this.derivativeSummary = this.derivativesSummaryDataService.mapDemandsToDerivativeSummary(
        this.demandCompany,
        this.demand,
        this.operationToHire
      );

      this.dateTimeHired = new Date().toISOString();

      this.modalHire
        ?.confirm(
          '',
          this.t(this.tk.Management.NdfHireQuote),
          this.t(this.tk.General.Confirm),
          this.t(this.tk.General.Cancel),
          '',
          'hire-operation'
        )
        .then(response => {
          if (response && this.dateTimeHired && this.operationHireForm.valid) {
            this.handleHireOperationConfirmation();
          } else {
            if (response) {
              this.handleHireOperation(operation);
              this.selectedOperation = operation;
              this.selectedOperationDate = this.formatDateStringToDate(
                operation.operationDate || ''
              );
              this.selectedOperationDueAt = this.formatDateStringToDate(operation.dueAt || '');
            } else {
              this.operationToHire = null;
              this.selectedOperationDate = null;
              this.selectedOperationDueAt = null;
            }
          }
        });
    };

    this.registerDemand(doHire);
  }

  handleHireOperationConfirmation() {
    this.modalHireConfirmation
      ?.confirm(
        '',
        this.t(this.tk.Management.NdfHireDateHourConfirmation),
        this.t(this.tk.General.Confirm),
        this.t(this.tk.General.Cancel),
        '',
        'hire-operation-confirmation'
      )
      .then(response => {
        if (response) {
          this.doHire(this.operationToHire as NdfDemandsCalculationsOperation);
        } else {
          this.selectedOperation = null;
          this.selectedOperationDate = null;
          this.selectedOperationDueAt = null;
        }
      });
  }

  handleQuoteOperationConfirmation(index: number) {
    this.modalQuoteConfirmation
      ?.confirm(
        '',
        this.t(this.tk.Management.NdfQuotationDateHourConfirmation),
        this.t(this.tk.General.Confirm),
        this.t(this.tk.General.Cancel),
        '',
        'quote-operation-confirmation'
      )
      .then(response => {
        if (response) {
          const operation = this.demand?.operations[index];

          if (!this.selectedOperation?.id) {
            if (this.selectedOperation) this.selectedOperation.id = operation?.id ?? '';
          }

          this.doQuotation(this.selectedOperation as NdfDemandsCalculationsOperation);
        } else {
          this.selectedOperation = null;
          this.selectedOperationDate = null;
          this.selectedOperationDueAt = null;
        }
      });
  }

  @ViewChild('modalQuoteConfirmation')
  modalQuoteConfirmation?: DialogComponent;

  @ViewChild('modalQuote')
  modalQuote?: DialogComponent;

  handleQuoteOperation(operation: NdfDemandsCalculationsOperation, index: number) {
    if (!this.checkIfDemandIsSavedAndThrowError()) {
      return;
    }
    const doQuote = () => {
      this.selectedOperation = operation;
      this.selectedOperationDate = this.formatDateStringToDate(operation.operationDate || '');
      this.selectedOperationDueAt = this.formatDateStringToDate(operation.dueAt || '');
      this.dateTimeQuoted = this.currentDate.toISOString();
      this.operationQuoteForm.patchValue({
        [this.operationQuoteHireProps.data]: this.dateTimeQuoted,
      });

      this.dateTimeQuoted = new Date().toISOString();

      this.modalQuote
        ?.confirm(
          '',
          this.t(this.tk.Management.NdfQuote),
          this.t(this.tk.General.Apply),
          this.t(this.tk.General.Cancel),
          '',
          'quote-operation'
        )
        .then(response => {
          if (response && this.dateTimeQuoted && this.operationQuoteForm.valid) {
            this.handleQuoteOperationConfirmation(index);
          } else {
            if (response) {
              console.log(this.demand?.operations[index]);
              this.selectedOperation = operation;
              this.selectedOperationDate = this.formatDateStringToDate(
                operation.operationDate || ''
              );
              this.selectedOperationDueAt = this.formatDateStringToDate(operation.dueAt || '');
              this.handleQuoteOperation(operation, index);
            } else {
              this.selectedOperation = null;
              this.selectedOperationDate = null;
              this.selectedOperationDueAt = null;
            }
          }
        })
        .catch(error => {
          console.error('Error: ', error);
        });
    };

    this.registerDemand(doQuote);
  }

  @ViewChild('modalCreateOperationsWithCog')
  modalCreateOperationsWithCog?: CreateOperatinsWithCogModalComponent;
  get hasOperationsNotSaved() {
    return this.demand?.operations.filter(x => x.id === null).length;
  }

  checkIfDemandIsSavedAndThrowError(): boolean {
    if (!this.demand?.id) {
      this.toast.showToastWarn(
        'A demanda precisa ser salva antes que você possa realizar outras ações.'
      );
      return false;
    }

    return true;
  }
  doHire(operation: NdfDemandsCalculationsOperation) {
    this.setIsLoading(true);
    this.ndfCalculatorService
      .putHireOperation(
        operation.id as string,
        this.dateTimeHired,
        this.operationHireForm.get(this.operationQuoteHireProps.bankOperatorName)?.value
      )
      .subscribe({
        next: () => {
          this.operationToHire = null;
          this.selectedOperationDate = null;
          this.selectedOperationDueAt = null;
          this.setIsLoading(false);
          this.toast.showToastSuccess(this.t(this.tk.Management.NdfHireSavedSuccessfully));
          this.getDemandById();
          this.disableFees = false;
        },
        error: error => {
          this.setIsLoading(false);
          console.error('Error hiring operation: ', error);

          this.toast.showToastError(
            this.t(this.tk.Management.ErrorUpdatingOperation),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  doQuotation(operation: NdfDemandsCalculationsOperation) {
    console.log('doQuotation: ', operation);
    if (!operation) return;
    this.setIsLoading(true);
    this.ndfCalculatorService
      .putQuoteOperation(
        operation.id as string,
        this.dateTimeQuoted,
        this.operationQuoteForm.get(this.operationQuoteHireProps.bankOperatorName)?.value
      )
      .subscribe({
        next: () => {
          this.setIsLoading(false);
          this.toast.showToastSuccess(this.t(this.tk.Management.OperationUpdatedSuccessfully));
          this.selectedOperation = null;
          this.selectedOperationDate = null;
          this.selectedOperationDueAt = null;
          this.getDemandById();
          this.disableFees = false;
        },
        error: error => {
          this.setIsLoading(false);
          console.error('Error quoting operation: ', error);
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorUpdatingOperation),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  public override setIsLoading(isLoading: boolean) {
    this.isFiltering = isLoading;
    this.utilsService.setIsLoading(isLoading);
    super.setIsLoading(isLoading);
  }

  removeOperation(operation: NdfDemandsCalculationsOperation) {
    if (this.demand?.id) {
      const modalRef = this.dialogService.open(ModalConfirmComponent, {
        data: {
          title: this.t(this.tk.General.RemoveOperation),
          description: this.t(this.tk.Management.ConfirmRemoveOperation),
          btnFirst: this.t(this.tk.General.Cancel),
          btnSecond: this.t(this.tk.General.Confirm),
        },
      });

      if (modalRef) {
        modalRef.onClose.subscribe({
          next: result => {
            if (result?.confirmed) {
              this.setIsLoading(true);
              this.ndfCalculatorService.deleteOperation(operation.id as string).subscribe({
                next: () => {
                  this.toast.showToastSuccess(
                    this.t(this.tk.Management.OperationRemovedSuccessfully),
                    ''
                  );
                  this.setIsLoading(false);
                  this.getDemandById();
                },
                error: error => {
                  this.setIsLoading(false);
                  console.error('Error removing operation: ', error);

                  this.toast.showToastError(
                    this.t(this.tk.Management.ErrorRemovingOperation),
                    error?.errors?.length
                      ? error.errors[0].message
                      : this.t(this.tk.General.RequestError)
                  );
                },
              });
            }
          },
        });
      }
    } else {
      this.toast.showToastError(this.t(this.tk.Management.RemoveOperationSaveDemand));
    }
  }

  handleOperationsFromCog(operations: DerivativeOperationQuoteFromAi[] | null) {
    if (!operations) return;

    const differentCurrencyOperations = operations.filter(
      x => x.currency != null && x.currency !== this.currencyCode
    );
    const validOperations = operations.filter(
      x => x.currency === this.currencyCode || x.currency == null
    );

    if (validOperations.length > 0) {
      this.toast.showToastSuccess(
        `${validOperations.length} ${this.t(this.tk.General.OperationsImportedSuccessfully)}`
      );
    }

    if (differentCurrencyOperations.length) {
      const operationCurrencies = [...new Set(differentCurrencyOperations.map(x => x.currency))];

      this.toast.showToastWarn(
        `${differentCurrencyOperations.length} ${this.t(this.tk.General.OperationsNotImportedDueToDifferentCurrencies)} (${operationCurrencies.join(', ')}) ${this.t(this.tk.General.CurrentDemandCurrency)} (${this.currencyCode})`
      );
    }

    // Mapeia as operações para o formato esperado pelo addSimulation
    const convertedOperations: NdfDemandsCalculationsOperation[] = validOperations.map(op => ({
      id: null,
      businessDaysCount: 0,
      daysCount: op.daysCount ?? 0,
      daysSinceHighestPTAX: 0,
      daysUntilLastPTAXExceedCap: 0,
      dueAt: op.dueAt ?? null,
      financialInstitution: null,
      financialInstitutionId: op.financialInstitutionId || '',
      lastDatePTAXExceededCap: '',
      nominalCost: 0,
      nominalCostAnnualized: 0,
      nominalCostAnnualizedRounded: 0,
      nominalCostFlat: 0,
      nominalCostFlatRounded: 0,
      notionalBRLByReferenceRate: 0,
      notionalBRLByReferenceRateRounded: 0,
      protectedVolumeUntilCap: 0,
      protectedVolumeUntilCapRounded: 0,
      operationDate:
        op.dueAt && op.daysCount
          ? new Date(new Date(op.dueAt).setDate(new Date(op.dueAt).getDate() - op.daysCount))
              .toISOString()
              .split('T')[0]
          : '',
      notionalAmount: op.notionalAmount ?? 0,
      forwardRate: op.forwardRate ?? 0,
      capValue: op.capValue !== undefined && op.capValue !== null ? op.capValue : 0,
      referenceExchangeRate: op?.referenceExchangeRate ?? 0,
      order:
        (this.demand?.operations?.reduce((max, op) => Math.max(max, op?.order ?? 0), 0) || 0) + 1,
    }));

    this.addSimulation(convertedOperations);

    return validOperations;
  }
  get currencyLabel(): string {
    return (
      this.internationalCurrencies.find(
        currency => currency.id === this.demand?.internationalCurrencyId
      )?.currencyCode || ''
    );
  }

  get formatedCnpj(): string {
    const value = this.demand?.company?.cnpj;
    return value ? CnpjFormatter.format(value) : '';
  }

  get operationTypeLabel(): string {
    const value = this.demand?.operationType;
    return this.getOperationTypeLabel(value as string);
  }

  getOperationTypeLabel(operationType: string): string {
    switch (operationType) {
      case 'Loan4131':
        return this.t(this.tk.Management.NdfLoan);
      case 'Import':
        return this.t(this.tk.Management.NdfImport);
      case 'Export':
        return this.t(this.tk.Management.NdfExport);
      case 'Indicative':
        return this.t(this.tk.Management.Indicative);
      default:
        return '';
    }
  }

  get valueMe(): string {
    const value = this.demand?.transactionValue || 0;
    return this.currencyPipe.transform(value, this.currencyLabel, 'symbol', '1.2-2') || '0';
  }

  get budgetRate(): string {
    const value = this.demand?.budgetRate || 0;
    return (value || '').toString() || '';
  }

  get demandLabel(): string {
    return this.demand?.operations?.length === 1
      ? this.t(this.tk.Management.NdfSimulation)
      : this.t(this.tk.Management.NdfSimulations);
  }

  private ensureAllOperationsInstallMentsRefresh(): void {
    try {
      const operationComponents = document.querySelectorAll('app-ndf-management-item');
      operationComponents.forEach((component: Element) => {
        if ('__ngContext__' in component) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const context = (component as any).__ngContext__;
          if (Array.isArray(context)) {
            const componentInstance = context.find(
              (node: unknown) =>
                node && typeof node === 'object' && 'ensureInstallmentsSelected' in node
            );
            if (
              componentInstance &&
              typeof componentInstance.ensureInstallmentsSelected === 'function'
            ) {
              componentInstance.ensureInstallmentsSelected();
            }
          }
        }
      });
    } catch (error) {
      console.error('Error syncing installments:', error);
    }
  }

  registerDemand(onSuccess: (() => void) | null = null) {
    if (this.operationsIsValid) {
      // Ensure all operations have their installments synchronized before saving
      this.ensureAllOperationsInstallMentsRefresh();

      const formatedDemand = this.formatDemand(this.demand as NdfDemandsCalculationsFiltered);
      if (this.demandId) {
        this.putDemand(formatedDemand, this.demandId, onSuccess);
      } else {
        this.postDemand(formatedDemand, onSuccess);
      }
    } else {
      this.toast.showToastWarn(this.t(this.tk.Management.IncompleteFieldsWarning));
    }
  }

  get operationsIsValid(): boolean {
    return (
      this.demand?.operations?.every(
        operation => operation.financialInstitutionId && operation.dueAt && operation.operationDate
      ) || false
    );
  }

  handleAnnualCostByScenarios(annualCostByScenarios: AnnualCostByScenarios[] = []) {
    if (this.demand) {
      this.demand = {
        ...this.demand,
        annualCostByScenarios: annualCostByScenarios,
      };

      const formatedDemand = this.formatDemand(this.demand);
      this.postSimulatedDemand(formatedDemand);
    }
  }

  putDemand(
    demandToSend: NdfDemandsCalculationsFiltered,
    demandId: string,
    onSuccess: (() => void) | null = null
  ): void {
    this.disableFees = true;
    this.setIsLoading(true);
    this.ndfCalculatorService.putDerivativesDemand(demandToSend, demandId).subscribe({
      next: response => {
        this.setIsLoading(false);
        if (response) {
          const annualCostByScenarios = response?.operations?.length
            ? response?.operations.find(operation => operation?.annualCostByScenarios)
                ?.annualCostByScenarios || []
            : demandToSend?.operations?.find(operation => operation?.annualCostByScenarios)
                ?.annualCostByScenarios || [];
          this.demand = {
            ...response,
            annualCostByScenarios,
            operations: response.operations.length
              ? response.operations
              : demandToSend?.operations || [],
          };
          this.handleNdfChart();
          if (this.store) {
            this.store.dispatch(
              setSelectedDemand({
                demand: this.demand,
              })
            );
          }
          this.saveDemandLocally(this.demand);
        }
        this.toast.showToastSuccess(this.t(this.tk.Management.NdfDemandSavedSuccessfully), '');
        this.editReference = false;
        this.getDemandById();

        if (onSuccess) {
          onSuccess();
        }
        this.disableFees = false;
      },
      error: error => {
        this.setIsLoading(false);
        console.error('putDerivativesDemand error: ', error);
        this.disableFees = false;
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorUpdatingDemand),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
      complete: () => {
        this.disableFees = false;
      },
    });
  }

  postDemand(
    demandToSend: NdfDemandsCalculationsFiltered,
    onSuccess: (() => void) | null = null
  ): void {
    this.disableFees = true;
    this.setIsLoading(true);
    this.ndfCalculatorService.postDerivativesDemand(demandToSend).subscribe({
      next: response => {
        const annualCostByScenarios = response?.operations?.length
          ? response?.operations.find(operation => operation?.annualCostByScenarios)
              ?.annualCostByScenarios || []
          : [];
        this.setIsLoading(false);
        if (response?.id) {
          if (this.store) {
            this.store.dispatch(
              setSelectedDemand({
                demand: {
                  ...response,
                  annualCostByScenarios,
                },
              })
            );
          }
          this.saveDemandLocally({
            ...response,
            annualCostByScenarios,
          } as NdfDemandsCalculationsFiltered);

          if (onSuccess) {
            onSuccess();
          }

          this.router.navigate(['/dashboard/control/derivative-management/derivatives/list'], {
            queryParams: { demandId: response?.id },
          });
        } else {
          this.cancelDemand();
        }
        this.disableFees = false;
      },
      error: error => {
        this.setIsLoading(false);
        console.error('postDerivativesDemand error: ', error);
        this.disableFees = false;

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorCreatingDemand),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
      complete: () => {
        this.setIsLoading(false);
        this.disableFees = false;
      },
    });
  }

  postSimulatedDemand(demandToSend: NdfDemandsCalculationsFiltered): void {
    console.log(
      'this.postSimulatedDemand',
      'postSimulatedDemand => ',
      demandToSend.sequentialId,
      demandToSend.operations.flatMap(x => ({
        dueAt: x.dueAt,
        operationDate: x.operationDate,
      }))
    );
    //this.setIsLoading(true);
    this.ndfCalculatorService.postDerivativesDemandSimulation(demandToSend).subscribe({
      next: response => {
        // Remove the loading indicator here too
        // this.setIsLoading(false);
        /*  this.toast.showToastSuccess(
            this.t(this.tk.Management.NdfDemandSimulationCreatedSuccessfully),
            ''
          )*/
        if (response) {
          this.demand = this.sortDemandByOrder(this.demand as NdfDemandsCalculationsFiltered);
          this.handleNdfChart();
          if (this.store) {
            this.store.dispatch(
              setSelectedDemand({
                demand: this.demand,
              })
            );
          }
          this.saveDemandLocally(this.demand);
        }
        this.disableFees = false;
      },
      error: error => {
        // Remove the loading indicator here too
        // this.setIsLoading(false);
        console.error('postDerivativesDemandSimulation error: ', error);
        this.demand = demandToSend;
        this.handleNdfChart();
        if (this.store) {
          this.store.dispatch(
            setSelectedDemand({
              demand: demandToSend,
            })
          );
        }
        // this.saveDemandLocally(demandToSend);
        this.disableFees = false;

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorCreatingDemandSimulation),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
      complete: () => {
        this.disableFees = false;
      },
    });
  }

  exportOperations() {
    const operationsId = this.demand?.operations.filter(x => x.selected).map(x => x.id);
    if (
      !operationsId ||
      !operationsId?.length ||
      operationsId!.filter(x => x !== null).length == 0
    ) {
      this.toast.showToastError(this.t(this.tk.Management.UnsavedOperationsWarning));
      return;
    }
    this.setIsLoading(true);

    this.derivativeReportHttpService
      .getOperationsSummaryReport(operationsId as string[])
      .pipe(
        first(),
        finalize(() => this.setIsLoading(false))
      )
      .subscribe({
        next: response => {
          if (response) {
            openFileInNewTab(response);
          }
          this.toast.showToastSuccess(this.t(this.tk.General.OperationsExportedSuccessfully));
        },
        error: () =>
          this.toast.showToastError(
            this.t(this.tk.Derivatives.ReportErrorTitle),
            this.t(this.tk.Derivatives.ReportErrorDescription)
          ),
        complete: () => {},
      });
  }
  addSimulation(operationsToAdd: NdfDemandsCalculationsOperation[] | null = null) {
    if (this.demand) {
      const newOperations = operationsToAdd ?? [this.generateNewOperation()];
      const demandToAdd = { ...this.demand };
      const operations = [...demandToAdd.operations, ...newOperations];
      this.demand = { ...demandToAdd, operations };
      this.demand = this.sortDemandByOrder(this.demand);
      setTimeout(() => {
        const div = document.querySelector('.ndf-management-grid__content');
        if (div) {
          div.scrollLeft = div.scrollWidth;
        }
      }, 500);
      this.applyFilter();
    }
  }

  generateNewOperation(): NdfDemandsCalculationsOperation {
    const data = {
      id: null,
      businessDaysCount: 0,
      daysCount: 0,
      daysSinceHighestPTAX: 0,
      daysUntilLastPTAXExceedCap: 0,
      dueAt: null,
      financialInstitution: null,
      financialInstitutionId: '',
      lastDatePTAXExceededCap: '',
      nominalCost: 0,
      nominalCostAnnualized: 0,
      nominalCostAnnualizedRounded: 0,
      nominalCostFlat: 0,
      nominalCostFlatRounded: 0,
      notionalBRLByReferenceRate: 0,
      notionalBRLByReferenceRateRounded: 0,
      protectedVolumeUntilCap: 0,
      protectedVolumeUntilCapRounded: 0,
      operationStrategyType: 'NDFCAP',
      order:
        (this.demand?.operations?.reduce((max, op) => Math.max(max, op?.order ?? 0), 0) || 0) + 1,
    } as NdfDemandsCalculationsOperation;

    return data;
  }

  handleCalendarValue(dates: string[], action: string): void {
    switch (action) {
      case 'quote':
        this.dateTimeQuoted = dates[0];
        this.operationQuoteForm.patchValue({
          [this.operationQuoteHireProps.data]: this.dateTimeQuoted,
        });
        break;
      case 'hire':
        this.dateTimeHired = dates[0];
        this.operationHireForm.patchValue({
          [this.operationQuoteHireProps.data]: this.dateTimeHired,
        });
        break;
    }
  }

  formatDateStringToDate(dateString: string): Date | null {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateRegex.test(dateString)) {
      return new Date(dateString);
    }
    return null;
  }

  formatDemand(demand: NdfDemandsCalculationsFiltered): NdfDemandsCalculationsFiltered {
    const {
      id,
      companyId,
      sequentialId,
      operationType,
      internationalCurrencyId,
      transactionValue,
      dueAt,
      reference,
      annualCostByScenarios,
      operations,
      budgetRate,
    } = demand;

    const verifiedAnnualCostByScenarios = (annualCostByScenarios || [])?.length
      ? annualCostByScenarios
      : operations.find(operation => operation?.annualCostByScenarios)?.annualCostByScenarios || [];

    return {
      id: id || null,
      companyId: companyId || null,
      sequentialId,
      operationType: operationType || '',
      internationalCurrencyId: internationalCurrencyId || '',
      transactionValue,
      dueAt: dueAt ? dueAt.split('T')[0] : null,
      reference: reference || '',
      annualCostByScenarios: verifiedAnnualCostByScenarios || [],
      budgetRate,
      // Usar os IDs salvos localmente para preservá-los
      comexOperationId: this.comexOperationId,
      loanOperationId: this.loanOperationId,
      operations: operations.map(operation => {
        const {
          id,
          operationDate,
          daysCount,
          businessDaysCount,
          financialInstitutionId,
          notionalAmount,
          forwardRate,
          capValue,
          reference,
          referenceExchangeRate,
          sequentialId,
          controlNameBlocked,
          annualCostByScenarios,
          order,
          colapsed,
          operationStrategyType,
          strikeBuy,
          strikeSell,
          premium,
          numberOfInstallments,
          activeIndexer,
          passiveIndexer,
          activeFixedRatePerYear,
          passiveFixedRatePerYear,
          dayCountConvention,
          installmentIds,
        } = operation;

        const test = {
          id: id || null,
          sequentialId: sequentialId || '',
          operationStatus: operation.operationStatus || 'Simulated',
          operationDate: operationDate
            ? operationDate.split('T')[0]
            : new Date().toISOString().split('T')[0],
          dueAt: operation.dueAt || null,
          daysCount: daysCount || 0,
          businessDaysCount: businessDaysCount || 0,
          financialInstitutionId: financialInstitutionId || '',
          notionalAmount: notionalAmount || 0,
          forwardRate: forwardRate || 0,
          capValue: capValue !== undefined && capValue !== null ? capValue : 0,
          referenceExchangeRate: referenceExchangeRate || 0,
          reference: reference || '',
          controlNameBlocked: controlNameBlocked || [],
          annualCostByScenarios: annualCostByScenarios || [],
          colapsed,
          order,
          operationStrategyType,
          strikeBuy,
          strikeSell,
          premium,
          numberOfInstallments,
          activeIndexer,
          passiveIndexer,
          activeFixedRatePerYear,
          passiveFixedRatePerYear,
          dayCountConvention,
          installmentIds,
        };

        return test;
      }),
    };
  }

  get currentDateISOString(): string {
    return this.currentDate.toISOString();
  }

  onFilterChange(type: 'operationStatus' | 'financialStatus' | 'composition', option: Option) {
    if (type === 'operationStatus') this.selectedOperationStatus = option;
    if (type === 'financialStatus') this.selectedFinancialStatus = option;
    if (type === 'composition') this.selectedComposition = option;
    this.applyFilter();
  }

  onTextFilterChange() {
    if (this.textFilterTimeout) {
      clearTimeout(this.textFilterTimeout);
    }
    this.textFilterTimeout = setTimeout(() => {
      const currentParams = { ...this.route.snapshot.queryParams };
      currentParams['filter'] = this.textFilter;

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: currentParams,
        replaceUrl: true,
      });

      this.applyFilter();
    }, 300);
  }

  private applyFilter(): void {
    this.isFiltering = true;
    //this.setIsLoading(true);
    setTimeout(() => {
      const operations = this.demand?.operations || [];
      // Remover filtro de texto do lado do componente, agora será feito no worker
      this.filterWorker?.postMessage({
        operations,
        filters: {
          status: this.selectedOperationStatus.value,
          financial: this.selectedFinancialStatus.value,
          composition: this.selectedComposition.value,
          textFilter: this.textFilter,
        },
      });
    }, 500);
  }

  // Getter para acessar o comex operation com fallback para o valor local
  get comexOperation() {
    return {
      sequentialId: this.demand?.comexOperation?.sequentialId || this.comexSequentialId,
    };
  }

  // Getter para acessar o loan operation com fallback para o valor local
  get loanOperation() {
    return {
      sequentialId: this.demand?.loanOperation?.sequentialId || this.loanSequentialId,
    };
  }

  // Método auxiliar para recuperar sequential ID do storage se necessário
  private getSequentialIdFromStorage(): string | undefined {
    // Este método não é mais necessário, mas mantemos para não quebrar a interface
    return undefined;
  }

  openLoanDetailsModal(loanOperationId: string): void {
    this.loanDetailsModalRef.open(loanOperationId);
  }

  openComexDetailsModal(comexOperationId: string): void {
    this.comexDetailsModalRef.open(comexOperationId);
  }

  openBetaVersion(): void {
    if (this.demandId) {
      this.router.navigate(['/dashboard/control/derivative-management/derivatives/list'], {
        queryParams: { demandId: this.demandId },
      });
    }
  }
}
